import {
	FORMULA_OPERATIONS as FP,
	GeometryTypes,
	IAdjustHandlerDirection,
	PathFillMode,
	PathOperationTypes,
	ShapeType,
} from "../widgets/interfaces/model/enumerations/index";
import { IPresetGeometry } from "../widgets/interfaces/model/geometry/PresetGeometry";

/**
 * Check mark preset definition. Check is not part of ECMA Office Open XML specs.
 * Currently we are adding check mark as a line.
 */
export const checkMark: IPresetGeometry = {

	ahLst: [
		{
			directionX: IAdjustHandlerDirection.REVERSE,
			gdRefX: "adj", maxX: 20753, minX: 0, pos: { x: "dx", y: "t" },
		},
	],

	avList: [
		{ name: "adj", fmla: { operation: FP.LiteralValue, operands: [5000] } },
	],

	gdLst: [
		{ name: "a", fmla: { operation: FP.PinTo, operands: [0, "adj", 50000] } },
		{ name: "z", fmla: { operation: FP.MultiplyDivide, operands: ["w", "a", 100000] } },
		{ name: "dx", fmla: { operation: FP.AddSubtract, operands: ["r", 0, "z"] } },
		{ name: "p", fmla: { operation: FP.AddSubtract, operands: ["vc", 0, 0] } },
		{ name: "q", fmla: { operation: FP.MultiplyDivide, operands: ["b", 20, 100] } },
		{ name: "qx", fmla: { operation: FP.MultiplyDivide, operands: ["r", 25, 100] } },
		{ name: "qy", fmla: { operation: FP.AddSubtract, operands: ["b", 0, "q"] } },
		{ name: "sl1x", fmla: { operation: FP.AddSubtract, operands: ["r", 0, "qx"] } },
		{ name: "sl1y", fmla: { operation: FP.AddSubtract, operands: ["t", 0, "qy"] } },
		{ name: "sl1", fmla: { operation: FP.MultiplyDivide, operands: ["sl1y", 1, "sl1x"] } },
		{ name: "fc1", fmla: { operation: FP.AddSubtract, operands: ["dx", 0, "qx"] } },
		{ name: "fc2", fmla: { operation: FP.MultiplyDivide, operands: ["fc1", "sl1", 1] } },
		{ name: "fy", fmla: { operation: FP.AddSubtract, operands: ["t", 0, "fc2"] } },
		{ name: "cx", fmla: { operation: FP.AddSubtract, operands: ["l", "z", 0] } },
		{ name: "sl2x", fmla: { operation: FP.AddSubtract, operands: ["qx", 0, "l"] } },
		{ name: "sl2y", fmla: { operation: FP.AddSubtract, operands: ["qy", 0, "p"] } },
		{ name: "sl2", fmla: { operation: FP.MultiplyDivide, operands: ["sl2y", 1, "sl2x"] } },
		{ name: "hc1", fmla: { operation: FP.AddSubtract, operands: ["fy", 0, "p"] } },
		{ name: "hc2", fmla: { operation: FP.MultiplyDivide, operands: ["hc1", 1, "sl2"] } },
		{ name: "hx", fmla: { operation: FP.AddSubtract, operands: ["qx", 0, "hc2"] } },
		{ name: "ic1", fmla: { operation: FP.AddSubtract, operands: ["hx", 0, "l"] } },
		{ name: "ic2", fmla: { operation: FP.MultiplyDivide, operands: ["ic1", "sl1", 1] } },
		{ name: "iy", fmla: { operation: FP.AddSubtract, operands: ["p", 0, "ic2"] } },
		{ name: "gc1", fmla: { operation: FP.AddSubtract, operands: ["qx", 0, "l"] } },
		{ name: "gc2", fmla: { operation: FP.MultiplyDivide, operands: ["gc1", "sl2", 1] } },
		{ name: "gy", fmla: { operation: FP.AddSubtract, operands: ["iy", "gc2", 0] } },
		{ name: "ec1", fmla: { operation: FP.AddSubtract, operands: ["r", 0, "qx"] } },
		{ name: "ec2", fmla: { operation: FP.MultiplyDivide, operands: ["ec1", "sl1", 1] } },
		{ name: "ey", fmla: { operation: FP.AddSubtract, operands: ["gy", "ec2", 0] } },
		// this is deviation from microsoft standards, introduced below terms for text padding inside shape
		// lp:- left padding, rp: right padding, tp: top padding, bp: bottom padding
		{ name: "lp", fmla: { operation: FP.AddSubtract, operands: ["l", 10, 0] } },
		{ name: "rp", fmla: { operation: FP.AddSubtract, operands: ["r", 0, 10] } },
		{ name: "tp", fmla: { operation: FP.AddSubtract, operands: ["t", 2, 0] } },
		{ name: "bp", fmla: { operation: FP.AddSubtract, operands: ["b", 0, 2] } },
	],

	pathLst: [
		// Adding dummy Path to make sure height of the path matches to height of the shape.
		// This is required for cropping.
		{
			// Make the fill none as we dont want to render it being dummy
			fill: PathFillMode.none,
			operations: [
				{ pt: { x: "l", y: "b" }, type: PathOperationTypes.MOVE_TO },
				{ pt: { x: "l", y: "b" }, type: PathOperationTypes.LINE_TO },
				{ type: PathOperationTypes.CLOSE },
			],
			// Make the stroke false as we dont want to have outline for this path being dummy
			stroke: false,

			// Note: As of now we are generating only one path element as we do not have deliverable shapes with more
			// than one path. However, specification provides way to mention more paths and renderer should care of
			// quantity when multiple paths are available.i.e.renderer should generate multiple SVG path elements per
			// model path.
			// For now, having no multiple path shapes, we are stick with one path only.
			// When we start supporting multiple paths, those may have different fill styles and may opt for stroke.
			// Adhering to those standards, we are adding fill as none and stroke as false to this dummy path so that
			// it does not get rendered even when we support multiple paths with varying fill and stroke.
		},
		// Following Path is rendering related points.
		{
			operations: [
				{ pt: { x: "l", y: "iy" }, type: PathOperationTypes.MOVE_TO },
				{ pt: { x: "qx", y: "gy" }, type: PathOperationTypes.LINE_TO },
				{ pt: { x: "r", y: "ey" }, type: PathOperationTypes.LINE_TO },
				{ pt: { x: "dx", y: "t" }, type: PathOperationTypes.LINE_TO },
				{ pt: { x: "qx", y: "fy" }, type: PathOperationTypes.LINE_TO },
				{ pt: { x: "hx", y: "p" }, type: PathOperationTypes.LINE_TO },
				{ type: PathOperationTypes.CLOSE },
			],
		}],
	presetShape: ShapeType.checkmark,
	rect: { l: "lp", t: "tp", r: "rp", b: "bp" },
	type: GeometryTypes.PRESET,
};
